import React, { memo, useCallback, useEffect, useState } from 'react'
import AudioPlayer from './AudioPlayer'
import AITryModal from './AITryModal'
import audioPeaks from '../data/audioPeaks.json'
import { getSessionEmail, getRemaining, updateUserUsage } from '../services/aiTryService'
import '../styles/ai-assistant-carousel.scss'

// Map "/audio/tti1.mp3" -> precomputed { peaks, duration }
const peaksFor = (src) => audioPeaks[src.split('/').pop().replace('.mp3', '')] || {}

const CARDS = [
  {
    variant: 'primary',
    name: 'tti-receptionist',
    heading: 'Textile Testing Labs — Lead Generation & Order Taking',
    props: [
      'Capture leads around the clock',
      'Handle multiple conversations simultaneously',
      'Initiate conversation with website visitors or social media users',
      'Tailor conversations based on user behavior and preferences',
      'Gather valuable data about potential leads',
    ],
    demos: [
      { src: '/audio/tti1.mp3', context: 'Customer asks about testing shirt quality, durability, and washability; requests info on pricing.' },
      { src: '/audio/tti2.mp3', context: 'Customer explores testing options for sports accessories and sample handover process.' },
    ],
  },
  {
    variant: 'primary',
    name: 'bolt-bistro',
    heading: 'Bolt Bistro — Receptionist & Order Taking',
    props: [
      'Place orders around the clock',
      'Handle multiple conversations simultaneously',
      'Take customer calls and guide them about the restaurant',
      'Tailor conversations based on user behavior and preferences',
      'Gather valuable data about potential leads',
    ],
    demos: [
      { src: '/audio/boltbistro1.mp3', context: 'Customer selects refreshing juice and chocolate cake after discussing available flavors and preferences.' },
      { src: '/audio/boltbistro2.mp3', context: 'Customer plans anniversary event with private space, classic decor, and diverse food preferences discussed.' },
    ],
  },
]

const InfoIcon = ({ variant }) => (
  <svg
    className="aac-demo__info-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={variant === 'secondary' ? 'rgba(234,232,230,0.7)' : 'rgba(32,32,32,0.5)'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
)

const AIAssistantCarousel = () => {
  const [active, setActive] = useState(0)
  const [animDir, setAnimDir] = useState(null)
  const [playingId, setPlayingId] = useState(null)
  const [stopTrigger, setStopTrigger] = useState(0)

  // Try-it-yourself modal orchestration
  const [modal, setModal] = useState({ open: false, view: 'gate', name: null, heading: null })
  const [pending, setPending] = useState(null) // action queued behind the email gate
  const [tries, setTries] = useState({}) // name -> { chat, call } remaining

  const isAudioPlaying = playingId !== null

  const refreshTries = useCallback(() => {
    const next = {}
    CARDS.forEach(c => { next[c.name] = getRemaining(c.name) })
    setTries(next)
  }, [])

  useEffect(() => { refreshTries() }, [refreshTries])

  // Increments usage then opens the demo window.
  // Returns true on success, or an error string to show the user.
  const proceed = useCallback(async (name, kind, heading) => {
    // Re-check against real usage (known only after login populates User Data).
    refreshTries()
    if (getRemaining(name)[kind] <= 0) {
      return kind === 'call'
        ? "You've already used your free voice call for this agent."
        : "You've used all your free chats for this agent."
    }
    const email = getSessionEmail()
    const ok = await updateUserUsage({ email, name, kind })
    refreshTries()
    if (ok) {
      setModal({ open: true, view: kind, name, heading })
      return true
    }
    return "Couldn't start the demo. Please try again."
  }, [refreshTries])

  const handleTry = useCallback((name, kind, heading) => {
    if (getRemaining(name)[kind] <= 0) return
    if (!getSessionEmail()) {
      setPending({ name, kind, heading })
      setModal({ open: true, view: 'gate', name, heading })
    } else {
      proceed(name, kind, heading)
    }
  }, [proceed])

  // Called by the email gate after verification; returns whether the demo opened.
  const handleVerified = useCallback(async () => {
    if (!pending) return false
    const { name, kind, heading } = pending
    setPending(null)
    return proceed(name, kind, heading)
  }, [pending, proceed])

  const closeModal = useCallback(() => {
    setModal(m => ({ ...m, open: false }))
    setPending(null)
    refreshTries()
  }, [refreshTries])

  const handlePlayStateChange = useCallback((id, playing) => {
    setPlayingId(prev => {
      if (playing) return id
      return prev === id ? null : prev
    })
  }, [])

  const go = useCallback((dir) => {
    // Stop any playing audio before navigating
    setStopTrigger(t => t + 1)
    setAnimDir(dir)
    setTimeout(() => {
      setActive(prev =>
        dir === 'next'
          ? (prev + 1) % CARDS.length
          : (prev - 1 + CARDS.length) % CARDS.length
      )
      setAnimDir(null)
    }, 220)
  }, [])

  // Pause auto-advance while audio is playing
  useEffect(() => {
    if (isAudioPlaying) return
    const id = setInterval(() => go('next'), 6000)
    return () => clearInterval(id)
  }, [go, isAudioPlaying])

  const goTo = useCallback((i) => {
    setStopTrigger(t => t + 1)
    setAnimDir('next')
    setTimeout(() => { setActive(i); setAnimDir(null) }, 220)
  }, [])

  const card = CARDS[active]
  const rem = tries[card.name] || { chat: 2, call: 1 }

  return (
    <section className="aac-section">
      <div className="aac-header">
        <h2 className="aac-heading">
          See Our AI Agents <span className="aac-heading__accent">in Action</span>
        </h2>
        <p className="aac-sub">
          Real conversations, real results. Listen to how our AI agents handle customer interactions across industries.
        </p>
      </div>

      <div className="aac-carousel">
        <button
          className="aac-arrow"
          onClick={() => go('prev')}
          aria-label="Previous card"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <div className={`aac-card-wrapper${animDir ? ` aac-card-wrapper--exit-${animDir}` : ''}`}>
          <div className={`aac-card aac-card--${card.variant}`}>
            {/* Top two-column area */}
            <div className="aac-card__top">
              <div className="aac-col-left">
                <h3 className="aac-card__heading">{card.heading}</h3>
                <ul className="aac-card__props">
                  {card.props.map((p, i) => (
                    <li key={i} className="aac-card__prop">{p}</li>
                  ))}
                </ul>
                <div className="aac-cta-col aac-cta-col--left">
                  <button
                    type="button"
                    className="aac-card__cta aac-card__cta--chat"
                    disabled={rem.chat <= 0}
                    onClick={() => handleTry(card.name, 'chat', card.heading)}
                  >
                    Try Chat
                  </button>
                  <span className="aac-cta-tries">{rem.chat} {rem.chat === 1 ? 'try' : 'tries'} left.</span>
                </div>
              </div>

              <div className="aac-divider" />

              <div className="aac-col-right">
                {card.demos.map((demo, i) => (
                  <div key={i} className="aac-demo">
                    <div className="aac-demo__context">
                      <InfoIcon variant={card.variant} />
                      <p className="aac-demo__text">{demo.context}</p>
                    </div>
                    <AudioPlayer
                      src={demo.src}
                      peaks={peaksFor(demo.src).peaks}
                      duration={peaksFor(demo.src).duration}
                      variant={card.variant}
                      id={`${active}-${i}`}
                      playingId={playingId}
                      stopTrigger={stopTrigger}
                      onPlayStateChange={handlePlayStateChange}
                    />
                  </div>
                ))}
                <div className="aac-cta-col aac-cta-col--right">
                  <button
                    type="button"
                    className="aac-card__cta aac-card__cta--call"
                    disabled={rem.call <= 0}
                    onClick={() => handleTry(card.name, 'call', card.heading)}
                  >
                    Try Voice Call
                  </button>
                  <span className="aac-cta-tries">{rem.call} {rem.call === 1 ? 'try' : 'tries'} left.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          className="aac-arrow"
          onClick={() => go('next')}
          aria-label="Next card"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      <div className="aac-dots">
        {CARDS.map((_, i) => (
          <button
            key={i}
            className={`aac-dot${i === active ? ' aac-dot--active' : ''}`}
            onClick={() => goTo(i)}
            aria-label={`Go to card ${i + 1}`}
          />
        ))}
      </div>

      <AITryModal
        open={modal.open}
        view={modal.view}
        name={modal.name}
        heading={modal.heading}
        onClose={closeModal}
        onVerified={handleVerified}
      />
    </section>
  )
}

export default memo(AIAssistantCarousel)
